import React from 'react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import { projects } from '../data/portfolio';
import { Github, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';

const ProjectsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-slate-100 text-slate-900 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 dark:text-slate-50">
      <Header />
      <main className="pt-20 sm:pt-24">
        <section
          aria-labelledby="projects-page-heading"
          className="py-12 sm:py-16"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h1
                id="projects-page-heading"
                className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl dark:text-slate-50"
              >
                All projects
              </h1>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                A focused selection of learning projects that show how Marco
                approaches frontend, mobile, and basic full-stack work. Each one
                is intentionally small, clear, and easy to understand for
                potential employers.
              </p>
            </div>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {projects.map((project, index) => (
                <motion.article
                  key={project.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.06, ease: 'easeOut' }}
                  className="glass-card flex flex-col bg-glass-light p-5 dark:bg-glass-dark"
                >
                  <div className="mb-4 h-32 rounded-xl bg-gradient-to-tr from-brand-pink/30 via-brand-purple/30 to-brand-orange/30" />
                  <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">
                    {project.title}
                  </h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    {project.description}
                  </p>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {project.highlight}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className="rounded-full bg-slate-900/5 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-50/5 dark:text-slate-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3 text-xs font-medium text-slate-600 dark:text-slate-300">
                    <span className="rounded-full bg-slate-900/90 px-3 py-1 text-[10px] uppercase tracking-wide text-slate-100 dark:bg-white dark:text-slate-900">
                      {project.role}
                    </span>
                    <div className="flex items-center gap-2">
                      {project.repoUrl && (
                        <a
                          href={project.repoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-[11px] text-slate-100 transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                        >
                          <span className="rounded-l-full bg-slate-800 px-1.5 py-1 dark:bg-slate-100">
                            <Github className="h-3 w-3" />
                          </span>
                          <span className="px-2 py-1">Code</span>
                        </a>
                      )}
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200/80 bg-white/80 px-3 py-1 text-[11px] text-slate-700 transition hover:border-brand-pink/70 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-brand-pink/70"
                        >
                          Live
                          <ArrowUpRight className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProjectsPage;



